import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import IntegrationIcon from "src/components/ui/IntegrationIcon";
import { ErrorAlert } from "src/components/ui/status-alert";
import { usePermissions } from "src/context/MeContext";
import { getProductName, isRootlyBranded } from "src/utils/brandTheme";

import type { PostgreSQLIntegrationDeleteMutation } from "./__generated__/PostgreSQLIntegrationDeleteMutation.graphql";
import type { PostgreSQLIntegrationQuery } from "./__generated__/PostgreSQLIntegrationQuery.graphql";
import PostgreSQLIntegrationForm from "./PostgreSQLIntegrationForm";

const postgresqlIntegrationQuery = graphql`
  query PostgreSQLIntegrationQuery {
    outboundIpAddresses
    me {
      ...appMe
      organization {
        integrations(integrationType: POSTGRESQL) {
          edges {
            node {
              id
              credentials
            }
          }
        }
      }
    }
  }
`;

const deleteIntegrationMutation = graphql`
  mutation PostgreSQLIntegrationDeleteMutation($input: DeleteIntegrationInput!) {
    deleteIntegration(input: $input) {
      success
      error
    }
  }
`;

interface PostgreSQLIntegrationProps {
  isOpen: boolean;
  onClose: () => void;
  onRefetch?: () => void;
  brandTheme?: string | null;
}

interface PostgreSQLCredentialsType {
  host?: string;
  port?: number;
  user?: string;
  password?: string;
  database?: string;
  sslmode?: string;
}

const PostgreSQLInstructions: React.FC<{
  productName: string;
  brandTheme?: string | null;
  outboundIpAddresses: readonly string[];
}> = ({ productName, brandTheme, outboundIpAddresses }) => {
  const prefix = isRootlyBranded(brandTheme) ? "rootly" : "tierzero";
  const roleName = `${prefix}_readonly`;
  const userName = `${prefix}_svc`;

  return (
    <div className="text-muted-foreground space-y-3">
      <p className="text-sm">Run the following SQL as a superuser on your PostgreSQL database.</p>

      <div className="mb-4">
        <p className="mb-2 font-bold">1. Create a Read-Only Role and User</p>
        <pre className="mt-1 p-2 bg-muted rounded text-xs overflow-x-auto">
{`-- Create a read-only role
CREATE ROLE ${roleName} NOLOGIN;

-- Grant read access to all existing tables
GRANT CONNECT ON DATABASE <your_database> TO ${roleName};
GRANT USAGE ON SCHEMA public TO ${roleName};
GRANT SELECT ON ALL TABLES IN SCHEMA public TO ${roleName};

-- Grant read access to future tables
ALTER DEFAULT PRIVILEGES IN SCHEMA public
  GRANT SELECT ON TABLES TO ${roleName};

-- Create a service user with the read-only role
CREATE USER ${userName} WITH PASSWORD '<strong_password>';
GRANT ${roleName} TO ${userName};`}
        </pre>
      </div>

      <div className="mb-4">
        <p className="mb-2 font-bold">2. Network Access</p>
        <p className="text-sm">
          Ensure your PostgreSQL server allows connections from {productName}&apos;s IP addresses:
        </p>
        <pre className="mt-1 p-2 bg-muted rounded text-xs overflow-x-auto">
{outboundIpAddresses.length > 0
  ? outboundIpAddresses.join("\n")
  : "IP addresses will be displayed once available."}
        </pre>
        <p className="text-sm mt-1">
          Add these IPs to your <code className="text-amber-600">pg_hba.conf</code> or security group rules.
        </p>
      </div>
    </div>
  );
};

export default function PostgreSQLIntegration({ isOpen, onClose, onRefetch, brandTheme }: PostgreSQLIntegrationProps) {
  const [showInstructions, setShowInstructions] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  const productName = getProductName(brandTheme);
  const { canWrite } = usePermissions();
  const canModify = canWrite("integrations");

  const data = useLazyLoadQuery<PostgreSQLIntegrationQuery>(
    postgresqlIntegrationQuery,
    {},
    { fetchPolicy: "network-only" },
  );

  const existingIntegration = data.me.organization?.integrations?.edges?.[0]?.node;
  const existingCredentials = existingIntegration?.credentials as PostgreSQLCredentialsType | null;
  const isConnected = !!existingIntegration;

  const [commitDelete, isDeleting] =
    useMutation<PostgreSQLIntegrationDeleteMutation>(deleteIntegrationMutation);

  const handleFormComplete = (success: boolean) => {
    if (success) {
      onClose();
      onRefetch?.();
    }
  };

  const handleFormCancel = () => {
    onClose();
  };

  const handleDisconnectConfirm = () => {
    if (!existingIntegration?.id) return;
    setDisconnectError(null);
    commitDelete({
      variables: { input: { id: existingIntegration.id } },
      onCompleted: (response) => {
        if (response.deleteIntegration.error) {
          setDisconnectError(response.deleteIntegration.error);
          return;
        }
        setShowDisconnectConfirm(false);
        onClose();
        onRefetch?.();
      },
      onError: (error) => {
        setDisconnectError(error.message);
      },
    });
  };

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl bg-popover text-foreground border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <IntegrationIcon type="POSTGRESQL" className="h-6 w-6" />
            {isConnected ? "Manage PostgreSQL" : "Connect PostgreSQL"}
          </DialogTitle>
          <DialogDescription>
            {isConnected
              ? `Update your PostgreSQL integration settings for ${productName}.`
              : `Connect your PostgreSQL database to give ${productName} access to query your data.`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 overflow-y-auto max-h-[80vh] p-1">
          <div className="space-y-3">
            <button
              onClick={() => setShowInstructions(!showInstructions)}
              className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
            >
              {showInstructions ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              {showInstructions ? "Hide instructions" : "Show instructions"}
            </button>

            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
              showInstructions
                ? "max-h-[2000px] opacity-100"
                : "max-h-0 opacity-0"
            }`}>
              <PostgreSQLInstructions productName={productName} brandTheme={brandTheme} outboundIpAddresses={data?.outboundIpAddresses ?? []} />
            </div>
          </div>

          <PostgreSQLIntegrationForm
            onComplete={handleFormComplete}
            onCancel={handleFormCancel}
            onDisconnect={isConnected && canModify ? () => setShowDisconnectConfirm(true) : undefined}
            integrationId={existingIntegration?.id}
            initialHost={existingCredentials?.host}
            initialPort={existingCredentials?.port}
            initialUser={existingCredentials?.user}
            initialDatabase={existingCredentials?.database}
            initialSslmode={existingCredentials?.sslmode}
          />
        </div>
      </DialogContent>
    </Dialog>

    <Dialog open={showDisconnectConfirm} onOpenChange={() => setShowDisconnectConfirm(false)}>
      <DialogContent className="sm:max-w-md bg-popover text-foreground border-border">
        <DialogHeader>
          <DialogTitle>Disconnect PostgreSQL</DialogTitle>
          <DialogDescription>
            This will remove the PostgreSQL integration and all associated credentials. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        {disconnectError && <ErrorAlert icon="none">{disconnectError}</ErrorAlert>}
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowDisconnectConfirm(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleDisconnectConfirm} disabled={isDeleting}>
            {isDeleting ? "Disconnecting..." : "Disconnect"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
